import { useState, useContext, useRef, useEffect } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { SnackBarContext } from "components/snack-bar";
import { FeatureFlagContext } from "context/featureFlagContext";
import { decrypt } from "pages/auth/utils";
import { CAMPAIGN_INTERNAL_STATUSES, COUPON_CODE_ERROR_MESSAGE } from "pages/constants";
import { CAMPAIGN_LOG_ENUM_TYPES } from "shared/constants";
import { apiClient } from "module/api";
import { getCurrencyCode } from "utils";
import { insertCampaignLogs } from "Logs/campaign/gql";
import { toFixed2 } from "utils/formators";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { updateSelfServeCampaignData } from "graphQL/mutations/selfServeCampaignData";
import { updateCampaign as updateCampaignDB } from "graphQL/mutations/campaign";
import { updateCampaignCityMetrics } from "graphQL/mutations/cityMetrics";
import { doCheckoutGQL } from "graphQL/query/cart";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { normalizeCampaignName } from "campaign-builder/state";
import { getPaymentSkipStatus } from "utils/paymentSkip";
import { useBillingPreview } from "campaign-builder/checkout/hooks/useBillingPreview";
import type { ChannelConfig } from "campaign-builder/config";
import { useTargeting, toSelectedCity } from "../targeting";
import { useChannelCostPreview } from "./cost/useChannelCostPreview";
import {
  deriveCheckoutDiscountChoice,
  CheckoutDiscountChoice,
} from "campaign-builder/submit/deriveCheckoutDiscountChoice";

interface UseChannelSubmitProps {
  config: ChannelConfig;
  setIsCampaignSubmissionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * HUB-440: the order drawer's useDiscountExclusivity.discountChoice, lifted up
   * via OrderDetailsDrawer's onDiscountChoiceChange. `undefined`/`null` until the
   * drawer's first effect runs (treated as "no discount") so submit never blocks
   * waiting on it. Mirrors useADMSubmit.
   */
  discountChoice?: CheckoutDiscountChoice;
}

/**
 * Channel-agnostic submission for the H2H/D2D order drawer. Mirrors ADM's new
 * billing pipeline (pathfinder costs → Stripe checkout via CheckoutContent, or a
 * direct skip-Stripe submit) but adds the per-city metrics + selectedCities the
 * flyering channels need, and never forces the ADM subtype. Cost comes from the
 * server-authoritative breakdown, not the legacy Zustand calculation.
 */
export const useChannelSubmit = ({
  config,
  setIsCampaignSubmissionModalOpen,
  discountChoice = null,
}: UseChannelSubmitProps) => {
  const runSnackBar = useContext(SnackBarContext);
  const history = useHistory();
  const { featureFlags, loading: flagsLoading } = useContext(FeatureFlagContext);
  const isReviewDiscountEnabled = !flagsLoading && !!featureFlags.isReviewDiscountEnabled;
  const [submitting, setSubmitting] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const { campaignData, clientId, user, refetchCampaign, country, setIsReadOnly } = useCampaignData();
  const { client } = useClientConfig();
  const { selectedCity, selectedAreas, baPerLocation } = useTargeting();

  const quoteItemLabel = config.orderDetails?.quoteItemLabel ?? config.label;
  const { campaignCostPreview, detailedCost, campaignDuration } = useChannelCostPreview(quoteItemLabel);
  const { tax, subtotal, total } = campaignCostPreview;

  const {
    id: campaignId,
    stripeCouponCode,
    startDate,
    flyerType,
    flyerWeight,
    campaignName,
    amountOff,
    percentOff,
  } = campaignData || {};

  const isPaymentSkipped = getPaymentSkipStatus(campaignData, client);
  const isD2D = config.channel === "D2D";
  const isSubmittingRef = useRef(false);

  // Stripe redirect-back: the platform appends ?payment=<encrypted-id> on return.
  // Mark read-only + surface the submission modal; Temporal moves the campaign to
  // IN_REVIEW server-side after the charge is authorized.
  useEffect(() => {
    const token = new URLSearchParams(history?.location?.search ?? "").get("payment") ?? undefined;
    if (token && campaignId) {
      const decryptedCampaignId = decrypt(token);
      window.history.replaceState(null, "", `${window.location.href.split("?payment")[0]}`);
      if (decryptedCampaignId && String(decryptedCampaignId) === String(campaignId)) {
        setIsReadOnly(true);
        setIsCampaignSubmissionModalOpen(true);
        refetchCampaign();
      } else {
        runSnackBar({ type: "error", msg: "Payment error.", vertical: "bottom", horizontal: "right" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history?.location?.search, campaignId]);

  const parsedStartDate = startDate ? `${moment(Number(startDate)).format("YYYY-MM-DD")}T00:00:00.000Z` : undefined;

  const campaignDetailedCosts = detailedCost
    ? {
        distribution: detailedCost.distribution,
        travel: detailedCost.travel,
        permit: detailedCost.permit,
        printing: detailedCost.printing,
        outfit: detailedCost.outfit,
        bonus: { quantity: 0, rate: 0, total: 0 },
        subtotal: toFixed2(subtotal),
        tax: toFixed2(tax),
        totalCost: toFixed2(total),
        amountOff: amountOff || 0,
        percentOff: percentOff || 0,
      }
    : null;

  const updatePathfinderData = async () => {
    await updateSelfServeCampaignData({
      campaignId,
      campaignDetailedCosts,
      // Resend the current selection alongside the costs (matches the legacy
      // flyering submit) so the cart is built from the persisted areas/missions.
      selectedCities: selectedCity ? [toSelectedCity(selectedCity, selectedAreas)] : [],
    });
  };

  const logCampaignSubmitted = async () => {
    await insertCampaignLogs([
      {
        campaignId,
        type: CAMPAIGN_LOG_ENUM_TYPES.CAMPAIGN_SUBMITTED,
        additionalInfo: `[CB] CAMPAIGN EDIT Subtotal: ${subtotal}, Amount off: ${amountOff}, Total: ${total}`,
      },
      {
        campaignId,
        type: CAMPAIGN_LOG_ENUM_TYPES.SET_PAYMENT_STATUS,
        additionalInfo: `[CB] Status: [uncaptured]`,
      },
    ]);
  };

  const updateCampaignAndCampaignExtraData = async () => {
    if (detailedCost && selectedCity) {
      await updateCampaignCityMetrics({
        campaignId,
        data: [
          {
            id: `newCity_1`,
            cityId: Number(selectedCity.id),
            totalFlyers: detailedCost.printing.quantity,
            totalMissions: detailedCost.distribution.quantity,
            distributionCost: detailedCost.distribution.total,
            travelCost: detailedCost.travel.total,
            permitCost: detailedCost.permit.total,
            printingCost: detailedCost.printing.total,
            outfitCost: detailedCost.outfit.total,
            postingCost: null,
            dataCost: null,
            bonusBudget: null,
            freightCost: null,
          },
        ],
      });
    }

    await updateCampaignDB({
      campaignId,
      campaignDurationWeek: campaignDuration,
      name: campaignName ? normalizeCampaignName(campaignName) : campaignName,
      printingCostPerFlyer: detailedCost?.printing.rate,
      // Persisted in cents, mirroring the legacy flyering submit.
      distributionCost: detailedCost ? detailedCost.distribution.rate * 100 : undefined,
    });

    await updateCampaignExtraData({
      campaignId,
      stripeCouponCode,
      startDate: parsedStartDate,
      flyerType,
      flyerWeight,
      campaignDuration,
      totalCosts: total,
      taxes: tax,
      submitterId: user?.id,
      flyersCount: detailedCost?.printing.quantity,
      baPerLocation: isD2D ? null : baPerLocation,
    });
  };

  const finalizeSubmission = async ({ doCheckout = false }: { doCheckout?: boolean }) => {
    try {
      setIsCampaignSubmissionModalOpen(true);
      if (doCheckout && campaignDetailedCosts) {
        try {
          await doCheckoutGQL({ campaignId, campaignDetailedCosts });
        } catch (error) {
          console.error("Failed to fetch cart data:", error);
          runSnackBar({
            type: "error",
            msg: "Failed to fetch cart information.",
            vertical: "bottom",
            horizontal: "right",
          });
          // Checkout failed — abort before marking the campaign IN_REVIEW, else
          // it would be submitted with no valid cart (undiagnosable for ops).
          setIsCampaignSubmissionModalOpen(false);
          return;
        }
      }
      await updateCampaignExtraData({
        campaignId,
        paymentStatus: "uncaptured",
        internalStatus: CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
        lastActiveStep: "submit",
      });
      refetchCampaign();
    } catch (e) {
      runSnackBar({
        type: "error",
        msg: "Error while submitting the campaign.",
        vertical: "bottom",
        horizontal: "right",
      });
    }
  };

  // Light re-check that an applied coupon is still live before charging (it was
  // fully validated when applied in the drawer; Stripe re-validates at capture).
  const assertCouponStillValid = async () => {
    if (!stripeCouponCode) return;
    const res = await apiClient.getPromotionCode({
      promotionCode: stripeCouponCode.trim(),
      countryCode: country?.code,
    });
    if (res.status !== 200) throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
    const { promotionCodes } = await res.json();
    if (!promotionCodes?.data?.[0]) throw new Error("Sorry, this coupon code is not active any more.");
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    // Never submit before the server cost has loaded — that would persist a
    // zero-dollar campaign. A zeroed breakdown is equally invalid (the server
    // returns all-zeros, not an error, when the persisted selection is missing).
    // The drawer's CTA also guards this, but keep the hook safe on its own.
    if (!detailedCost || detailedCost.subtotal <= 0) {
      runSnackBar({
        type: "error",
        msg: "Still loading your quote. Please try again in a moment.",
        vertical: "bottom",
        horizontal: "right",
      });
      return;
    }
    try {
      isSubmittingRef.current = true;
      await assertCouponStillValid();
      await updatePathfinderData();
      // Log before refetch so the price values aren't cleared mid-write.
      await logCampaignSubmitted();
      await updateCampaignAndCampaignExtraData();
      if (!isPaymentSkipped) {
        setShowCheckout(true);
      } else {
        await finalizeSubmission({ doCheckout: true });
      }
    } catch (error) {
      runSnackBar({
        type: "error",
        msg: `Failed to submit campaign. ${(error as any)?.message || ""}`,
        vertical: "bottom",
        horizontal: "right",
      });
      throw error;
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const onSubmit = async () => {
    if (isSubmittingRef.current || submitting) return;
    setSubmitting(true);
    try {
      await handleSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  const currency = country ? getCurrencyCode(country) : "USD";
  // HUB-440: same intent the ADM checkout sends as `discount_choice`, derived from
  // the drawer's exclusivity choice + the flag, so it's `true` only when the review
  // discount is genuinely the order's chosen discount. Flows to the billing preview
  // and (via OrderDetailsFlow → CheckoutContent) the charge/initiate call.
  // HUB-440: never signal the review discount to the backend when checkout is
  // skipped — the skip path (doCheckoutGQL) carries no discount, so applying it
  // would only ever mislead. The drawer also hides the offer in this case.
  const applyReviewDiscount =
    !isPaymentSkipped &&
    deriveCheckoutDiscountChoice({
      isReviewDiscountEnabled,
      discountChoice,
      hasStripeCoupon: !!stripeCouponCode,
    }) === "review";
  const { costBreakdown, breakdownReady } = useBillingPreview({
    subtotal,
    currency,
    campaignId,
    couponCode: stripeCouponCode,
    applyReviewDiscount,
    showCheckout,
    onError: () => {
      runSnackBar({
        type: "error",
        msg: "Couldn't load price breakdown. Please try again.",
        vertical: "bottom",
        horizontal: "right",
      });
    },
  });

  return {
    onSubmit,
    submitting,
    showCheckout,
    setShowCheckout,
    costBreakdown,
    breakdownReady,
    couponCode: stripeCouponCode || undefined,
    applyReviewDiscount,
    campaignId: campaignId || "",
    countryCode: country?.code,
  };
};
